<template>
  <mf-loading-dialog :loading="isLoading">
    <div>
      <v-container v-show="!confirmRaffle" class="pa-1" fluid>
        <v-container class="py-0 px-2" fluid>
          <v-row no-gutters>
            <v-col cols="12">
              <v-row>
                <v-col class="px-0">
                  <title-description
                    id="raffle-title"
                    :disable="false"
                    :title.sync="raffle.title"
                    :description.sync="raffle.description"
                    :status="formattedRaffleStatus"
                    :counter="titleDescriptionCounter"
                    :title-error="getSafe(() => $v.raffle.title.$error)"
                    :title-error-message="errorTitle"
                    :disableTitle="isDisabled"
                    :disableDescription="isDisabled"
                    :description-error="getSafe(() => $v.raffle.description.$error)"
                    :description-error-message="errorDescription"
                  />
                </v-col>
              </v-row>
              <units-and-rules :errors="errorsUnitsAndRules" :is-disabled="isDisabled" :read-file="getFileBase64" :rulesFile="rulesFile" />
              <v-row class="align-stretch">
                <v-col :cols="showExclusionSegments ? 4 : 6" class="pr-1 pl-0">
                  <inclusion-segments-configuration class="fill-height" :segments.sync="raffle.segments" :disabled="isDisabled" />
                </v-col>
                <v-col v-if="showExclusionSegments" cols="4" class="pr-1 pl-1">
                  <exclusion-segments-configuration class="fill-height" :segments.sync="raffle.exclusion_segments" :disabled="isDisabled" />
                </v-col>
                <v-col :cols="showExclusionSegments ? 4 : 6" class="pr-0 pl-1">
                  <app-exhibition-and-start-date class="fill-height" :errors="errorsExhibitionDateAndStartDate" :is-disabled="isDisabled" />
                </v-col>
              </v-row>

              <v-row>
                <v-container fluid class="px-0">
                  <v-card>
                    <v-card-title>Descrição do sorteio no app </v-card-title>
                    <v-card-text>
                      <v-textarea
                        v-model="raffle.app_description"
                        class="textarea"
                        :error-messages="errorsAppDescription"
                        :disabled="isDisabled"
                        placeholder="Descrição do sorteio no app..."
                        outlined
                        no-resize
                        rows="5"
                        counter="1000"
                      />
                    </v-card-text>
                  </v-card>
                </v-container>
              </v-row>
              <banner-row
                :is-disabled="isDisabled"
                :validations="$v"
                :rules-value-tooltip="rulesValueTooltip"
                :rules-value-text="rulesValueText"
                :errors-rules-value="errorsRulesValue"
                :raffle-value.sync="raffleValue"
              />
              <div class="header-section pt-3">
                <h1 class="mb-2">Geração de números da sorte</h1>
                <p class="mb-0">Configurações sobre a padronização do formato dos números da sorte.</p>
              </div>
              <raffle-registry-mode :is-disabled="isDisabled" :validations="$v" :raffle-value.sync="raffleValue" />
              <div class="header-section pt-3">
                <h1 class="mb-2">Sorteio e Prêmios</h1>
                <p class="mb-0">Configurações sobre os cupons e os prêmios do sorteio.</p>
              </div>
              <raffle-rules-configuration
                :is-disabled="isDisabled"
                :validations="$v"
                :error-max-numbers-per-person="getErrorMaxNumbersPerPerson()"
                :error-max-numbers-per-coupon="getErrorMaxNumbersPerCoupon()"
                :rules-value-tooltip="rulesValueTooltip"
                :rules-value-text="rulesValueText"
                :errors-rules-value="errorsRulesValue"
                :raffle-value.sync="raffleValue"
                :raffle-cumulative.sync="raffleCumulative"
              />
              <participating-products
                v-if="canConfigureProducts"
                :products.sync="raffle.rules.products"
                :eans.sync="eans"
                :status-campaign="raffle.status"
                :editable="raffle.status === 'DRAFT'"
                :products-error="getSafe(() => $v.raffle.rules.products.$error)"
                :is-disabled="isDisabled"
                :acceleration-operation.sync="raffle.rules.acceleration_operation"
                :acceleration-value.sync="raffle.rules.acceleration_value"
                :acceleration-value-error="getSafe(() => $v.raffle.rules.acceleration_value.$error)"
                @update-eans="updateEans"
                v-on:eans="getEans"
              />
              <participating-product-groups
                v-if="canConfigureProductGroups"
                :product-groups.sync="raffle.rules.product_groups"
                :error="getSafe(() => $v.raffle.rules.product_groups.$error)"
                :is-disabled="isDisabled"
              />
              <prizes-table
                :open-prize-dialog="openPrizeDialog"
                :is-disabled="isDisabled"
                :error-prize="errorPrize"
                :set-editing-prize="setEditingPrize"
                :delete-prize="deletePrize"
              />
            </v-col>
          </v-row>
        </v-container>
        <v-row justify="center">
          <prize-dialog
            v-if="showPrizeDialog"
            :is-editing-prize="isEditingPrize"
            :save="savePrize"
            :prize="prize"
            :read-file="getFileBase64"
            :prize-file="prizeFile"
            @input="closePrizeDialog"
          ></prize-dialog>
        </v-row>

        <mf-action-buttons
          :primary-button="primaryButton"
          :secondary-button="secondaryButton"
          :extra-button="extraButton"
          :cancel-button="{
            text: 'Cancelar',
            action: cancel,
            isVisible: true,
            isLoading: false,
          }"
        />
      </v-container>
      <confirm-raffle
        v-show="confirmRaffle"
        :is-submitting="isSubmitting"
        :eans.sync="eans"
        v-on:eans="getEans"
        @goBack="setConfirmRaffle(false)"
        @submit="submit(false)"
      />
      <raffle-term-warning v-model="showTermWarning" />
    </div>
  </mf-loading-dialog>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import findIndex from 'lodash/findIndex'
import moment from 'moment-timezone'
import TitleDescription from '@/modules/campanhas/views/components/edit/TitleDescription.vue'
import { getRaffleValidations } from '../../helpers/validators'
import { formatMoney, preventNumberEvents } from '../../helpers'
import PrizeDialog from './PrizeDialog.vue'
import UnitsAndRules from './UnitsAndRules.vue'
import AppExhibitionAndStartDate from './AppExhibitionDate.vue'
import BannerRow from './BannerRow.vue'
import PrizesTable from './PrizesTable.vue'
import { MUTATION_DELETE_RAFFLE_DRAFT } from '../../graphql'
import { MUTATION_SINGLE_UPLOAD_BASE64 } from '@/graphql'
import { convertByteToMegabyte } from '@/config/helpers.js'
import ConfirmRaffle from './ConfirmRaffle.vue'
import ParticipatingProducts from './ParticipatingProducts.vue'
import ParticipatingProductGroups from './ParticipatingProductGroups.vue'
import { raffleMixin } from '@/modules/mobile/sorteio/views/mixins/raffle'

export default {
  name: 'EditRaffleInfo',
  components: {
    TitleDescription,
    PrizeDialog,
    UnitsAndRules,
    AppExhibitionAndStartDate,
    BannerRow,
    PrizesTable,
    ConfirmRaffle,
    ParticipatingProducts,
    ParticipatingProductGroups,
    InclusionSegmentsConfiguration: () => import('@/modules/mobile/sorteio/views/components/InclusionSegmentsConfiguration.vue'),
    ExclusionSegmentsConfiguration: () => import('@/modules/mobile/sorteio/views/components/ExclusionSegmentsConfiguration.vue'),
    RaffleRegistryMode: () => import('@/modules/mobile/sorteio/views/components/RaffleRegistryMode.vue'),
    RaffleRulesConfiguration: () => import('@/modules/mobile/sorteio/views/components/RaffleRulesConfiguration.vue'),
    RaffleTermWarning: () => import('@/modules/mobile/sorteio/views/components/RaffleTermWarning.vue'),
  },
  mixins: [raffleMixin],
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
    isNewRaffle: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    raffleId: '',
    showTermWarning: false,
    onApprovalVersion: false,
    isSubmitting: false,
    isDraft: false,
    disableButtons: false,
    titleDescriptionCounter: {
      title: 70,
      description: 150,
    },
    hasBanner: false,
    rulesFile: {
      file: null,
      fileName: '',
      fileType: null,
      allowedFileTypes: ['application/pdf'],
      fileUrl: null,
      fileContent: null,
      isLoading: false,
    },
    prizeFile: {
      file: null,
      fileName: '',
      fileType: null,
      allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png'],
      fileUrl: null,
      fileContent: null,
      isLoading: false,
    },
    showPrizeDialog: false,
    isEditingPrize: false,
    eans: [],
    confirmRaffle: false,
    prize: {
      title: '',
      image_url: null,
      raffle_date: null,
      quantity: null,
    },
  }),
  computed: {
    ...mapState({
      raffle: (state) => state.sorteio.raffle,
    }),
    showExclusionSegments() {
      return this.$getAllowedModules()['raffle_segment_exclusion']
    },
    showApprovalWarning() {
      return this.onApprovalVersion || this.raffle?.on_approval
    },
    approvalWarningProps() {
      return { variant: this.onApprovalVersion ? 'warning' : 'info' }
    },
    approvalWarningContent() {
      if (this.onApprovalVersion) {
        return `<span style="color: #B58943">Você está visualizando uma versão do sorteio que ainda requer aprovação pela equipe da Mercafacil. Para visualizar a versão em vigor <a href="#/mobile/sorteios/${this.raffleId}">clique aqui</a>.</span>`
      } else if (this.raffle?.on_approval) {
        return `<span style="color: rgb(0, 77, 153)">Você está visualizando um sorteio que possui uma versão em aprovação pela equipe da Mercafacil. Para visualizar a versão em aprovação <a href="#/mobile/sorteios/${this.raffleId}?on_approval_version=true">clique aqui</a>.</span>`
      } else {
        return ''
      }
    },
    extraButton() {
      if (this.raffle.status === 'DRAFT' && this.isEditingRaffle) {
        return {
          text: 'Apagar rascunho',
          action: () => this.deleteDraft(),
          isVisible: true,
        }
      }
      if (this.raffle.status === 'SCHEDULED') {
        return {
          text: 'Suspender',
          action: () => this.unscheduleRaffle(),
          isVisible: true,
        }
      }
      return {}
    },
    primaryButton() {
      if (this.raffle.status === 'DRAFT') {
        return {
          text: 'Ativar sorteio',
          action: () => this.setConfirmRaffle(true),
          isVisible: true,
        }
      } else if (!['FINISHED'].includes(this.raffle.status)) {
        return {
          text: 'Salvar sorteio',
          action: () => this.setConfirmRaffle(true),
          isVisible: true,
        }
      }
      return {}
    },
    secondaryButton() {
      if (this.raffle.status === 'DRAFT') {
        return {
          text: this.isEditingRaffle ? 'Salvar alterações' : 'Salvar rascunho',
          action: () => this.submit(true),
          isVisible: true,
        }
      } else {
        return {
          text: 'Duplicar',
          action: () => this.cloneRaffle(this.raffle._id, this.raffle.title),
          isVisible: true,
        }
      }
    },
    confirmRaffleButtonLabel() {
      if (!this.raffle._id || this.raffle.status === 'DRAFT') return 'ATIVAR SORTEIO'
      return 'SALVAR ALTERAÇÕES'
    },
    raffleValue: {
      get() {
        if (!this.raffle.rules.value) return 0
        if (this.raffle.rules.type !== 'BY_SELECTED_ITEMS_QUANTITY') {
          return formatMoney(this.raffle.rules.value)
        }
        return this.raffle.rules.value
      },
      set(value) {
        if (!value) return 0
        if (this.raffle.rules.type !== 'BY_SELECTED_ITEMS_QUANTITY') {
          const formattedValue = formatMoney(value)
          this.setRaffleValue(formattedValue)
        } else {
          this.setRaffleValue(parseInt(value))
        }
      },
    },
    raffleCumulative: {
      get() {
        if (!this.raffle.rules.cumulative) return false
        if (this.raffle.rules.type !== 'BY_COUPON_TOTAL_VALUE') return false
        return this.raffle.rules.cumulative
      },
      set(value) {
        if (this.raffle.rules.type !== 'BY_COUPON_TOTAL_VALUE') {
          this.setRaffleCumulative(false)
        } else {
          this.setRaffleCumulative(value)
        }
      },
    },
    isEditingRaffle() {
      return this.$route.params.id !== undefined
    },
    formattedRaffleStatus() {
      if (this.onApprovalVersion) {
        return 'ON_APPROVAL'
      } else if (this.raffle.status === 'ONGOING') {
        return 'ACTIVE'
      } else {
        return this.raffle.status
      }
    },
    errorTitle() {
      if (!this.$v.raffle.title) return ''
      if (this.$v.raffle.title.$error) {
        if (!this.$v.raffle.title.required) return 'Dê um nome para esta campanha.'
        else if (!this.$v.raffle.title.minLength) return 'O título possui menos de 2 caracteres.'
        else if (!this.$v.raffle.title.maxLength) return 'O título excedeu 70 caracteres.'
        else if (!this.$v.raffle.title.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    errorDescription() {
      if (!this.$v.raffle.description) return ''
      if (this.$v.raffle.description.$error) {
        if (!this.$v.raffle.description.minLength) return 'A descrição possui menos de 2 caracteres.'
        else if (!this.$v.raffle.description.maxLength) return 'A descrição excedeu 150 caracteres.'
        else if (!this.$v.raffle.description.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    errorsUnitsAndRules() {
      const errorUnits = this.getUnitsError()
      const errorRules = this.getErrorRulesFile()
      const errorTermsOptin = this.getErrorTermsOptin()
      return {
        errorUnits,
        errorRules,
        errorTermsOptin,
      }
    },
    errorsExhibitionDateAndStartDate() {
      const errorStartDate = this.getStartDateError()
      const errorStartExhibitionDate = this.getErrorStartExhibitionDate()
      return {
        errorStartDate,
        errorStartExhibitionDate,
      }
    },
    errorsAppDescription() {
      if (!this.$v.raffle.app_description) return ''
      if (this.$v.raffle.app_description.$error) {
        if (!this.$v.raffle.app_description.required) return 'Informe detalhes para melhor entender o sorteio.'
        else if (!this.$v.raffle.app_description.minLength) return 'A descrição possui menos de 7 caracteres.'
        else if (!this.$v.raffle.app_description.maxLength) return 'A descrição excedeu 1000 caracteres.'
        else if (!this.$v.raffle.app_description.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    errorsRulesValue() {
      if (!this.$v.raffle.rules.value) return ''
      if (this.$v.raffle.rules.value.$error) {
        if (!this.$v.raffle.rules.value.required) {
          switch (this.raffle.rules.type) {
            case 'BY_COUPON_TOTAL_VALUE':
            case 'BY_COUPON_TOTAL_VALUE_PARTICIPANT_PRODUCT':
            case 'BY_COUPON_TOTAL_VALUE_ACCELERATOR_PRODUCT':
            case 'BY_COUPON_TOTAL_VALUE_ACCELERATOR_PRODUCT_GROUPS_WITH_CUMULATIVE':
              return 'Valor da compra pra concorrer a um número da sorte deve ser fornecido.'
            case 'BY_SELECTED_ITEMS_QUANTITY':
              return 'Quantidade mínima dos produtos pra concorrer a um número da sorte deve ser fornecida.'
            case 'BY_SELECTED_ITEMS_VALUE':
              return 'Valor de produtos selecionados da compra pra concorrer a um número da sorte deve ser fornecido.'
            default:
              return ''
          }
        } else if (!this.$v.raffle.rules.value.minValue) {
          return 'O valor mínimo deve ser maior que 0.'
        }
      }
      return ''
    },
    errorPrize() {
      if (!this.$v.raffle.prizes) return ''
      if (this.$v.raffle.prizes.$error) {
        if (!this.$v.raffle.prizes.minLength) return 'Ao menos um prêmio deve ser registrado.'
        if (!this.$v.raffle.prizes.moreThanFiveDaysFromStartDate) return 'A data de sorteio dos prêmios deve ser pelo menos 7 dias depois do início do sorteio.'
      }
      return ''
    },
    rulesValueText() {
      switch (this.raffle.rules.type) {
        case 'BY_COUPON_TOTAL_VALUE':
          return 'Valor da compra pra ganhar um número da sorte'
        case 'BY_SELECTED_ITEMS_QUANTITY':
          return 'Quantidade mínima de produtos pra ganhar um número da sorte'
        case 'BY_SELECTED_ITEMS_VALUE':
          return 'Valor dos produtos elegíveis para ganhar um número da sorte'
        case 'BY_COUPON_TOTAL_VALUE_PARTICIPANT_PRODUCT':
          return 'Valor da compra pra ganhar um número da sorte'
        case 'BY_COUPON_TOTAL_VALUE_ACCELERATOR_PRODUCT':
          return 'Valor da compra pra ganhar um número da sorte'
        default:
          return ''
      }
    },
    rulesValueTooltip() {
      switch (this.raffle.rules.type) {
        case 'BY_COUPON_TOTAL_VALUE':
          return 'Valor exato de compra que garante ao consumidor um número da sorte. Nessa modalidade um cupom pode gerar mais de um número da sorte.'
        case 'BY_SELECTED_ITEMS_QUANTITY':
          return 'A quantidade inserida abaixo se refere a todos os itens presentes na listagem, não sendo necessário que o consumidor leve todos itens iguais. ex: caso o número abaixo seja 5, o consumidor não precisa levar 5 itens iguais, pode levar 3 de um SKU e 2 de outro que irá receber um número da sorte.'
        case 'BY_SELECTED_ITEMS_VALUE':
          return ' O valor inserido abaixo se refere a todos os itens presentes na listagem, não sendo necessário que o consumidor leve o valor total de apenas um item. ex: caso o número abaixo seja R$50, o consumidor não precisa levar R$50 em um mesmo produto, pode levar R$30 de um item e R$20 em outro item para receber um número da sorte.'
        case 'BY_COUPON_TOTAL_VALUE_PARTICIPANT_PRODUCT':
          return 'Valor mínimo do cupom com algum dos produtos selecionados para que o consumidor receba um número da sorte. Essa modalidade não é cumulativa, um cupom gera apenas um número da sorte.'
        case 'BY_COUPON_TOTAL_VALUE_ACCELERATOR_PRODUCT':
          return 'Valor exato de compra que garante ao consumidor um número da sorte. Nessa modalidade um cupom pode gerar mais de um número da sorte. Caso o consumidor leve algum dos produtos aceleradores, ele ganhará números da sorte extras.'
        case 'BY_COUPON_TOTAL_VALUE_ACCELERATOR_PRODUCT_GROUPS_WITH_CUMULATIVE':
          return 'Valor exato de compra que garante ao consumidor um número da sorte. Nessa modalidade um cupom pode gerar mais de um número da sorte. Caso o consumidor leve produtos suficientes dos grupos aceleradores, ele ganhará números da sorte extras.'
        default:
          return ''
      }
    },
    isDisabled() {
      return ['FINISHED', 'ONGOING'].includes(this.raffle.status)
    },
  },
  watch: {
    '$route.query': {
      handler: function () {
        this.initRaffle()
      },
      deep: true,
    },
  },
  mounted() {
    this.initRaffle()
  },
  methods: {
    ...mapMutations(['setRaffleValue', 'setRaffleCumulative', 'setRaffleDates']),
    preventDefault(event, type) {
      return preventNumberEvents(event, type)
    },
    ...mapActions(['upsertRaffle']),
    initRaffle() {
      const raffleType = this.$route.query['raffle-type']
      this.raffleId = this.$route.params.id
      this.onApprovalVersion = this.$route.query.on_approval_version === 'true'
      if (!this.isEditingPrize && !raffleType && !this.raffleId) this.$router.push(`/mobile/sorteios`).catch(() => {})
    },
    computeRaffleDates() {
      const startDate = this.raffle.prizes.reduce((acc, item) => {
        if (!acc) return item.eligibility_start_date
        if (moment(item.eligibility_start_date) < moment(acc)) {
          return item.eligibility_start_date
        }
        return acc
      }, null)
      const endDate = this.raffle.prizes.reduce((acc, item) => {
        if (!acc) return item.eligibility_end_date
        if (moment(item.eligibility_end_date) > moment(acc)) {
          return item.eligibility_end_date
        }
        return acc
      }, null)
      this.setRaffleDates({ startDate, endDate })
    },
    async deleteDraft() {
      const confirmed = await this.$confirm({
        title: 'Remover campanha',
        message: `Tem certeza que deseja remover a campanha "${this.raffle.title}"?`,
        confirmText: 'Remover',
        confirmColor: 'vermelho-d20',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      await this.$apollo
        .mutate({
          mutation: MUTATION_DELETE_RAFFLE_DRAFT,
          variables: {
            raffle_id: this.raffle._id,
          },
        })
        .then(() => {
          this.$router.push(`/mobile/sorteios`)
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.delete'))
        })
    },
    async unscheduleRaffle() {
      const confirmed = await this.$confirm({
        title: 'Suspender sorteio',
        message: 'O sorteio será movido para o status rascunho',
        confirmText: 'Ok',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.isSubmitting = true
      try {
        const context = {
          apollo: this.$apollo,
          isDraft: true,
        }
        await this.upsertRaffle(context)
        this.$snackbar({ message: 'Sucesso ao suspender campanha', snackbarColor: 'success' })
        this.$router.push(`/mobile/sorteios`).catch(() => {})
      } catch (error) {
        console.log(error)
        this.isSubmitting = false
        this.$snackbar({ message: 'Erro ao suspender campanha', snackbarColor: 'error' })
      }
    },
    openPrizeDialog() {
      this.showPrizeDialog = true
    },
    closePrizeDialog() {
      this.showPrizeDialog = false
      this.cleanUpPrize()
    },
    cleanUpPrize() {
      this.prize = {
        title: '',
        image_url: null,
        raffle_date: null,
        quantity: null,
      }
      this.prizeFile = {
        file: null,
        fileName: '',
        fileType: null,
        allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png'],
        fileUrl: null,
        fileContent: null,
        isLoading: false,
      }
    },
    setEditingPrize(pageIndex, { itemsPerPage, page }) {
      const prizeIndex = (page - 1) * itemsPerPage + pageIndex
      const editingPrize = this.raffle.prizes[prizeIndex]
      this.prize = {
        ...this.prize,
        ...editingPrize,
        isEditing: true,
        index: prizeIndex,
      }
      this.prizeFile.fileUrl = this.prize.image_url
      this.openPrizeDialog()
    },
    deletePrize(pageIndex, { itemsPerPage, page }) {
      const prizeIndex = (page - 1) * itemsPerPage + pageIndex
      this.raffle.prizes.splice(prizeIndex, 1)
      this.computeRaffleDates()
    },
    updateEans(eansProduct, group) {
      const indexGroup = findIndex(this.raffle.rules.products, group)
      const { eans, sku } = eansProduct

      if (group.isGroup) {
        const indexProduct = this.raffle.rules.products[indexGroup].products.findIndex((product) => product.sku === sku)
        this.raffle.rules.products[indexGroup].products[indexProduct].eans = eans
      } else {
        this.raffle.rules.products[indexGroup].eans = eans
      }
    },
    getEans(value) {
      this.eans = value
    },
    validate() {
      this.$v.$reset()
      this.$v.$touch()
      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })
        return false
      }
      return true
    },
    setConfirmRaffle(value) {
      if (value) {
        this.isDraft = false
        if (!this.$getAllowedModules()['raffle_signed_terms']) {
          this.showTermWarning = true
          return
        }
        if (!this.validate()) return
      }
      this.confirmRaffle = value
      this.$emit('changePage', value)
    },
    async submit(isDraft) {
      if (this.isSubmitting) return
      this.isSubmitting = true
      let snackMessage
      this.isDraft = isDraft
      if (this.isDraft && !this.validate()) {
        this.isSubmitting = false
        return
      }
      if (isDraft) {
        snackMessage = this.isEditingRaffle ? 'salvar alterações no rascunho de sorteio.' : 'salvar rascunho de sorteio.'
      } else if (!isDraft && this.raffle.draft && this.$getIsAdmin()) {
        snackMessage = 'criar novo sorteio.'
      } else if (!isDraft && this.raffle.draft && !this.$getIsAdmin()) {
        snackMessage = 'submeter novo sorteio para aprovação.'
      } else if (this.$getIsAdmin()) {
        snackMessage = 'editar sorteio.'
      } else {
        snackMessage = 'submeter edição do sorteio para aprovação.'
      }
      try {
        const context = {
          apollo: this.$apollo,
          isDraft,
          isAdmin: this.$getIsAdmin(),
        }
        await this.upsertRaffle(context)
        this.$snackbar({ message: `Sucesso ao ${snackMessage}`, snackbarColor: 'success' })
        this.$router.push(`/mobile/sorteios`).catch(() => {})
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: `Erro ao ${snackMessage}`, snackbarColor: 'error' })
      } finally {
        this.isSubmitting = false
      }
    },
    cancel() {
      this.$router.push(`/mobile/sorteios`).catch(() => {})
    },
    getFileUrl(base64, fileName) {
      return this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: {
            filename: fileName,
            base64: base64,
          },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(
          ({
            data: {
              singleUploadBase64: { url },
            },
          }) => {
            return url
          }
        )
        .catch((error) => {
          console.log(error)
          throw new Error(error)
        })
    },
    async getFileBase64(event, targetFile) {
      if (event.target.files.length === 0) return
      const file = event.target.files[0]
      this[targetFile].isLoading = true
      if (!this[targetFile].allowedFileTypes.includes(file.type.trim())) {
        this.$snackbar({
          message: `Permitido apenas arquivos em ${this[targetFile].allowedFileTypes.join(', ')} o arquivo tem o formato: ${file.type.trim()}`,
          snackbarColor: 'error',
        })
        this[targetFile].isLoading = false
        return
      }
      const maxFileSize = targetFile === 'rulesFile' ? 10 : 1
      if (convertByteToMegabyte(event.target.files[0].size) > maxFileSize) {
        this.$snackbar({ message: `Permitido apenas arquivos de até ${maxFileSize} MB`, snackbarColor: 'error' })
        this[targetFile].isLoading = false
        return
      }
      this[targetFile].fileType = file.type.trim()
      this[targetFile].file = file
      this[targetFile].fileName = file.name
      let reader = new FileReader()
      reader.onload = async () => {
        try {
          this[targetFile].fileContent = reader.result
          if (targetFile === 'rulesFile') {
            this.raffle.rules_url = await this.getFileUrl(this[targetFile].fileContent, this[targetFile].fileName)
          } else {
            this.prize.image_url = await this.getFileUrl(this[targetFile].fileContent, this[targetFile].fileName)
          }
          this[targetFile].isLoading = false
        } catch (err) {
          this[targetFile].fileContent = null
          this.$snackbar({ message: 'Não foi possível salvar o arquivo, tente novamente', snackbarColor: 'error' })
          this[targetFile].isLoading = false
        }
      }
      reader.readAsDataURL(this[targetFile].file)
    },
    savePrize() {
      const { index, isEditing, ...prize } = this.prize
      prize.winners = []
      if (!isEditing) {
        this.raffle.prizes.push(prize)
      } else {
        this.raffle.prizes.splice(index, 1, prize)
      }
      this.cleanUpPrize()
      this.computeRaffleDates()
    },
    getUnitsError() {
      if (!this.$v.raffle.units) return false
      if (this.$v.raffle.units.$error) {
        return true
      }
    },
    getStartDateError() {
      if (!this.$v.raffle.start_date) return ''
      if (this.$v.raffle.start_date.$error) {
        if (!this.$v.raffle.start_date.required) return 'Informe uma data de início.'
        else if (!this.$v.raffle.start_date.isFuture) return 'A data de início deve ser maior que a atual.'
        return ''
      }
    },
    getErrorStartExhibitionDate() {
      if (!this.$v.raffle.app_start_exhibition_date) return ''
      if (this.$v.raffle.app_start_exhibition_date.$error) {
        if (!this.$v.raffle.app_start_exhibition_date.lessOrEqualStartDate)
          return 'A data de início de exibição no app deve ser menor ou igual a de início do sorteio.'
      }
      return ''
    },
    getErrorRulesFile() {
      if (!this.$v.raffle.rules_url) return ''
      if (this.$v.raffle.rules_url.$error) {
        if (!this.$v.raffle.rules_url.required) return 'Arquivo PDF com regras do sorteio é obrigatório.'
        return ''
      }
    },
    getErrorTermsOptin() {
      if (!this.$v.raffle.optin) return ''
      if (this.$v.raffle.optin.terms.$error) {
        if (!this.$v.raffle.optin.terms.required) return 'O termo não pode ser vazio.'
        else if (!this.$v.raffle.optin.terms.maxLength) return 'O termo excedeu 10000 caracteres.'
        else if (!this.$v.raffle.optin.terms.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
        return ''
      }
    },
    getErrorMaxNumbersPerPerson() {
      if (!this.$v.raffle.max_numbers_per_person) return ''
      if (this.$v.raffle.max_numbers_per_person.$error) {
        if (!this.$v.raffle.max_numbers_per_person.minValue) return 'O valor mínimo deve ser maior que 0.'
      }
      return ''
    },
    getErrorMaxNumbersPerCoupon() {
      if (!this.$v.raffle.max_numbers_per_coupon) return ''
      if (this.$v.raffle.max_numbers_per_coupon.$error) {
        if (!this.$v.raffle.max_numbers_per_coupon.minValue) return 'O valor mínimo deve ser maior que 0.'
      }
      return ''
    },
    async cloneRaffle(id, name) {
      const confirmed = await this.$confirm({
        ...this.$t('campanhas.confirmations.clone'),
        message: `Deseja duplicar ${name ? 'a campanha ' + name.trim().bold() : 'esta campanha'}?`,
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.$router.push({ name: `Sorteio`, params: { cloneRaffleId: id } }).catch(() => {})
    },
  },
  validations() {
    return getRaffleValidations(this.raffle, this.isDraft)
  },
}
</script>

<style lang="scss">
.header-section {
  h1 {
    color: $cinza-d10;
    font-size: 22px;
    font-weight: 700;
    line-height: 27px;
  }
  p {
    font-size: 14px;
    color: $cinza-0;
    line-height: 20px;
  }
}
</style>
